import React, { useEffect, useState } from 'react'
import { X } from 'lucide-react'
import './FilterModal.css'

interface Category {
  id: string
  name: string
  subcategories?: Category[]
  card_ids?: number[]
}

interface FilterModalProps {
  isOpen: boolean
  onClose: () => void
  categories: Array<Category>
  activeSection?: string
  activeSubsection?: string
  activeSubSubsection?: string
  onFilterApply?: (
    sectionId: string,
    subsectionId: string,
    subSubsectionId?: string
  ) => void
}

const FilterModal = ({
  isOpen,
  onClose,
  categories,
  activeSection,
  activeSubsection,
  activeSubSubsection,
  onFilterApply,
}: FilterModalProps) => {
  const [selectedCategory, setSelectedCategory] = useState<string>('')
  const [selectedSubcategory, setSelectedSubcategory] = useState<string>('')
  const [selectedSubSubcategory, setSelectedSubSubcategory] =
    useState<string>('')
  const [isClosing, setIsClosing] = useState<boolean>(false)
  // Инициализация значений при открытии модального окна
  useEffect(() => {
    if (isOpen) {
      setIsClosing(false)
      setSelectedCategory(activeSection || '')
      setSelectedSubcategory(activeSubsection || '')
      setSelectedSubSubcategory(activeSubSubsection || '')
    }
  }, [isOpen])

  // Обновление значений при изменении пропсов (только если не закрываемся)
  useEffect(() => {
    if (isOpen && !isClosing) {
      setSelectedCategory(activeSection || '')
      setSelectedSubcategory(activeSubsection || '')
      setSelectedSubSubcategory(activeSubSubsection || '')
    }
  }, [activeSection, activeSubsection, activeSubSubsection, isOpen, isClosing])

  // Блокировка скролла body
  useEffect(() => {
    if (isOpen) {
      const scrollY = window.scrollY
      document.body.style.position = 'fixed'
      document.body.style.top = `-${scrollY}px`
      document.body.style.width = '100%'
    } else {
      const scrollY = document.body.style.top
      document.body.style.position = ''
      document.body.style.top = ''
      document.body.style.width = ''
      if (scrollY) {
        window.scrollTo(0, parseInt(scrollY || '0', 10) * -1)
      }
    }

    return () => {
      document.body.style.position = ''
      document.body.style.top = ''
      document.body.style.width = ''
    }
  }, [isOpen])

  // Закрытие по клавише Escape
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        handleClose()
      }
    }

    if (isOpen) {
      document.addEventListener('keydown', handleEscape)
      return () => document.removeEventListener('keydown', handleEscape)
    }
  }, [isOpen])

  if (!isOpen) return null

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      handleClose()
    }
  }

  const handleCategorySelect = (categoryId: string) => {
    setSelectedCategory(categoryId)
    setSelectedSubcategory('')
    setSelectedSubSubcategory('')
  }

  const handleSubcategorySelect = (subcategoryId: string) => {
    setSelectedSubcategory(subcategoryId)
    setSelectedSubSubcategory('')
    // Убираем автоматическое применение фильтра - теперь только через кнопку "Применить"
  }

  const handleSubSubcategorySelect = (subSubcategoryId: string) => {
    setSelectedSubSubcategory(subSubcategoryId)
    // Убираем автоматическое применение фильтра - теперь только через кнопку "Применить"
  }

  const handleApplyFilter = () => {
    onFilterApply?.(
      selectedCategory,
      selectedSubcategory,
      selectedSubSubcategory
    )
    handleClose()
  }

  const handleClose = () => {
    if (isClosing) return // Предотвращаем повторные вызовы

    setIsClosing(true)
    setTimeout(() => {
      onClose()
      setIsClosing(false)
    }, 300) // Время анимации
  }

  const clearFilters = () => {
    setSelectedCategory('')
    setSelectedSubcategory('')
    setSelectedSubSubcategory('')
    onFilterApply?.('', '', '')
    handleClose()
  }

  const getHeaderTitle = () => {
    return 'Фильтры'
  }

  const getSelectedCategoryName = () => {
    const category = categories.find(c => c.id === selectedCategory)
    return category?.name || ''
  }

  const getSelectedSubcategoryName = () => {
    const category = categories.find(c => c.id === selectedCategory)
    const subcategory = category?.subcategories?.find(
      s => s.id === selectedSubcategory
    )
    return subcategory?.name || ''
  }

  // Функция для подсчета общего количества карточек в категории и всех её подкатегориях
  const getTotalCardCount = (category: Category): number => {
    let count = 0

    // Считаем карточки в самой категории
    if (category.card_ids && category.card_ids.length > 0) {
      count += category.card_ids.length
    }

    // Рекурсивно считаем карточки во всех подкатегориях
    if (category.subcategories && category.subcategories.length > 0) {
      category.subcategories.forEach(subcategory => {
        count += getTotalCardCount(subcategory)
      })
    }

    return count
  }

  // Функция для фильтрации категорий с карточками
  const filterCategoriesWithCards = (categoryList: Category[]): Category[] => {
    return categoryList.filter(category => getTotalCardCount(category) > 0)
  }

  const renderCategoryStep = () => {
    const categoriesWithCards = filterCategoriesWithCards(categories)

    return (
      <div className="filter-section">
        <div className="filter-options">
          {categoriesWithCards.map(category => (
            <button
              key={category.id}
              className={`filter-option ${selectedCategory === category.id ? 'active' : ''}`}
              onClick={() => handleCategorySelect(category.id)}
            >
              {category.name}
            </button>
          ))}
        </div>
      </div>
    )
  }

  const renderSubcategoryStep = () => {
    const category = categories.find(c => c.id === selectedCategory)
    if (!category?.subcategories) return null

    const subcategoriesWithCards = filterCategoriesWithCards(
      category.subcategories
    )
    if (subcategoriesWithCards.length === 0) return null

    return (
      <div className="filter-section">
        <h3 className="filter-section-title">Категория</h3>

        <div className="filter-options">
          {subcategoriesWithCards.map(subcategory => (
            <button
              key={subcategory.id}
              className={`filter-option filter-subcategory ${selectedSubcategory === subcategory.id ? 'active' : ''}`}
              onClick={() => handleSubcategorySelect(subcategory.id)}
            >
              {selectedSubcategory === subcategory.id && (
                <X
                  size={20}
                  className="filter-option-close"
                  onClick={e => {
                    e.stopPropagation()
                    setSelectedSubcategory('')
                    setSelectedSubSubcategory('')
                  }}
                />
              )}
              {subcategory.name}
            </button>
          ))}
        </div>
      </div>
    )
  }

  const renderSubSubcategoryStep = () => {
    const category = categories.find(c => c.id === selectedCategory)
    const subcategory = category?.subcategories?.find(
      s => s.id === selectedSubcategory
    )
    if (!subcategory?.subcategories) return null

    const subSubcategoriesWithCards = filterCategoriesWithCards(
      subcategory.subcategories
    )
    if (subSubcategoriesWithCards.length === 0) return null

    return (
      <div className="filter-section">
        <h3 className="filter-section-title">Подкатегория</h3>

        <div className="filter-options">
          {subSubcategoriesWithCards.map(subSubcategory => (
            <button
              key={subSubcategory.id}
              className={`filter-option filter-subsubcategory ${selectedSubSubcategory === subSubcategory.id ? 'active' : ''}`}
              onClick={() => handleSubSubcategorySelect(subSubcategory.id)}
            >
              {selectedSubSubcategory === subSubcategory.id && (
                <X
                  size={20}
                  className="filter-option-close"
                  onClick={e => {
                    e.stopPropagation()
                    setSelectedSubSubcategory('')
                  }}
                />
              )}
              {subSubcategory.name}
            </button>
          ))}
        </div>
      </div>
    )
  }

  return (
    <div
      className={`filter-modal-overlay ${isClosing ? 'closing' : ''}`}
      onClick={handleOverlayClick}
    >
      <div className={`filter-modal ${isClosing ? 'closing' : ''}`}>
        <div className="filter-modal-header">
          <h2 className="filter-modal-title">{getHeaderTitle()}</h2>
          <button
            className="filter-modal-close"
            onClick={handleClose}
            aria-label="Закрыть"
          >
            <X size={24} />
          </button>
        </div>

        <div className="filter-modal-content">
          {renderCategoryStep()}
          {selectedCategory && renderSubcategoryStep()}
          {selectedCategory &&
            selectedSubcategory &&
            renderSubSubcategoryStep()}

          {/* Показываем текущий выбор */}
        </div>

        <div className="filter-modal-actions">
          <button
            className="filter-show-button"
            onClick={handleApplyFilter}
            disabled={!selectedCategory}
          >
            Показать
          </button>
          <button className="filter-clear-button" onClick={clearFilters}>
            Очистить фильтры
          </button>
        </div>
      </div>
    </div>
  )
}

export default FilterModal
